import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import letterService from '../../services/letterService';
import { publicTemplates } from '../../services/adminService';
import {
    EMPTY_ANSWERS,
    EMPTY_CONTENT,
    LETTER_TEMPLATES,
    LetterData,
} from '../../types/letter';
import LetterPreview from './LetterPreview';

const LetterGallery: React.FC = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const letterId = params.get('id');
    const { logout } = useAuth();
    const [letter, setLetter] = useState<LetterData | null>(null);
    const [selected, setSelected] = useState('letter-formel');
    const [color, setColor] = useState('#0d9488');
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const [catalog, setCatalog] = useState(LETTER_TEMPLATES);

    useEffect(() => {
        publicTemplates('letter')
            .then((list: any[]) => {
                if (!Array.isArray(list) || !list.length) return;
                const bySlug = new Map(list.map((t) => [t.slug || t.id, t]));
                const merged = LETTER_TEMPLATES.map((t) => {
                    const meta = bySlug.get(t.id);
                    if (!meta) return null;
                    if (meta.isActive === false) return null;
                    return {
                        ...t,
                        name: meta.name || t.name,
                        hex: meta.hex || t.hex,
                        isNew: !!meta.isNew || !!meta.isNewBadge,
                    };
                }).filter(Boolean) as typeof LETTER_TEMPLATES;
                if (merged.length) setCatalog(merged);
            })
            .catch(() => undefined);
    }, []);

    useEffect(() => {
        if (!letterId) {
            navigate('/creer-lettre', { replace: true });
            return;
        }
        letterService
            .get(letterId)
            .then((l) => {
                setLetter(l);
                setSelected(l.template || 'letter-formel');
                setColor(l.style?.primaryColor || '#111827');
            })
            .catch((e) => setError(e?.response?.data?.error || 'Lettre introuvable'))
            .finally(() => setLoading(false));
    }, [letterId, navigate]);

    const previewData: LetterData = useMemo(() => {
        const base: LetterData = letter || {
            template: selected,
            title: 'Aperçu',
            answers: {
                ...EMPTY_ANSWERS,
                firstName: 'Jean',
                lastName: 'Kouassi',
                email: 'jean.kouassi@email.ci',
                phone: '+225 07 00 00 00 00',
                address: 'Cocody, Abidjan',
                jobTitle: 'Chargé de clientèle',
            },
            content: EMPTY_CONTENT,
            style: { primaryColor: color, fontFamily: 'Georgia, serif' },
        };
        return {
            ...base,
            template: selected,
            style: { ...(base.style || {}), primaryColor: color },
        };
    }, [letter, selected, color]);

    const colors = ['#0d9488', '#1e3a5f', '#166534', '#c2410c', '#7c2d12', '#334155'];

    const choose = async () => {
        if (!letterId) return;
        setGenerating(true);
        setError('');
        try {
            await letterService.update(letterId, {
                template: selected,
                style: {
                    primaryColor: color,
                    fontFamily:
                        selected === 'letter-formel'
                            ? 'Arial, Helvetica, sans-serif'
                            : 'Georgia, serif',
                },
            });
            await letterService.generate(letterId);
            navigate(`/lettre/${letterId}`);
        } catch (e: any) {
            setError(e?.response?.data?.error || e.message || 'Génération impossible');
        } finally {
            setGenerating(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-teal-700" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <header className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
                <button type="button" onClick={() => navigate('/espace')} className="text-xl font-bold">
                    Flash<span className="text-teal-600">CV</span>
                </button>
                <p className="text-sm text-slate-600 hidden sm:block">
                    Choisissez un modèle adapté au marché ivoirien
                </p>
                <button type="button" onClick={logout} className="p-2 text-slate-500">
                    <LogOut className="w-5 h-5" />
                </button>
            </header>

            <div className="flex flex-col lg:flex-row">
                <aside className="lg:w-56 bg-white border-r border-slate-100 p-5 space-y-6">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                            Couleur
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {colors.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setColor(c)}
                                    className={`w-8 h-8 rounded-full border-2 ${
                                        color === c ? 'border-slate-900 scale-110' : 'border-transparent'
                                    }`}
                                    style={{ backgroundColor: c }}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="space-y-2">
                        {catalog.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => {
                                    setSelected(t.id);
                                    setColor(t.hex);
                                }}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm border ${
                                    selected === t.id
                                        ? 'border-teal-600 bg-teal-50'
                                        : 'border-transparent hover:bg-slate-50'
                                }`}
                            >
                                <span className="font-semibold text-slate-800 inline-flex items-center gap-2">
                                    {t.name}
                                    {'isNew' in t && (t as { isNew?: boolean }).isNew && (
                                        <span className="text-[9px] font-black uppercase bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full">
                                            New
                                        </span>
                                    )}
                                </span>
                                <span className="block text-xs text-slate-500">{t.description}</span>
                            </button>
                        ))}
                    </div>
                </aside>

                <main className="flex-1 p-6 overflow-auto">
                    {error && (
                        <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm px-4 py-3">
                            {error}
                        </div>
                    )}
                    <div className="flex gap-5 overflow-x-auto pb-4 items-start">
                        {catalog.map((t) => {
                            const scale = 0.28;
                            const cardW = Math.round(794 * scale);
                            const cardH = Math.round(1123 * scale);
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => {
                                        setSelected(t.id);
                                        setColor(t.hex);
                                    }}
                                    className={`shrink-0 relative rounded-lg overflow-hidden border-2 transition bg-white ${
                                        selected === t.id
                                            ? 'border-teal-600 shadow-lg'
                                            : 'border-slate-200 hover:border-slate-300'
                                    }`}
                                    style={{ width: cardW }}
                                >
                                    {selected === t.id && (
                                        <span className="absolute top-2 right-2 z-10 bg-teal-600 text-white rounded-full p-1">
                                            <Check className="w-3 h-3" />
                                        </span>
                                    )}
                                    <div
                                        className="overflow-hidden pointer-events-none bg-white"
                                        style={{ width: cardW, height: cardH }}
                                    >
                                        <div
                                            style={{
                                                width: 794,
                                                height: 1123,
                                                transform: `scale(${scale})`,
                                                transformOrigin: 'top left',
                                            }}
                                        >
                                            <LetterPreview
                                                data={{
                                                    ...previewData,
                                                    template: t.id,
                                                    style: {
                                                        ...previewData.style,
                                                        primaryColor: selected === t.id ? color : t.hex,
                                                    },
                                                }}
                                                template={t.id}
                                                showPlaceholders
                                            />
                                        </div>
                                    </div>
                                    <div className="px-3 py-2 text-left border-t border-slate-100 flex items-center justify-between gap-2">
                                        <p className="text-sm font-semibold text-slate-800">{t.name}</p>
                                        {'isNew' in t && (t as { isNew?: boolean }).isNew && (
                                            <span className="text-[9px] font-black uppercase bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full shrink-0">
                                                New
                                            </span>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </main>
            </div>

            <footer className="px-6 pt-2 pb-8 flex items-center justify-center gap-4 relative z-[80]">
                <button
                    type="button"
                    onClick={() => navigate(letterId ? `/creer-lettre?id=${letterId}` : '/creer-lettre')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300"
                >
                    <ArrowLeft className="w-4 h-4" /> Retour
                </button>
                <button
                    type="button"
                    disabled={generating}
                    onClick={choose}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-teal-700 text-white font-medium hover:bg-teal-800 disabled:opacity-50"
                >
                    {generating ? (
                        <>En cours de chargement…</>
                    ) : (
                        <>Choisir ce modèle & générer</>
                    )}
                </button>
            </footer>
        </div>
    );
};

export default LetterGallery;
